import React from "react";

import { FrameViews } from "@com.mgmtp.a12.client/client-core/lib/core/frame";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { NavigationContentboxContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/contentbox/main/action-contentbox/action-contentbox.view";
import { VisibleView } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/master-detail/main/master-detail.api";
import { MasterDetail } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/master-detail/main/master-detail.view";
import { SizeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector/main/size-context";

import { CHANGELOG_ACTIVITY } from "../../modules/changelog/constants";

import { MobileNavigationContext } from "../mobileNavigationContext";

const EXTRA_VIEW_ACTIVITY_IDS = [CHANGELOG_ACTIVITY];

export const StammdatenMasterDetailLayout: FrameViews.Layout = {
	component: function StammdatenMasterDetailLayout(props: FrameViews.LayoutProps) {
		const currentSize = React.useContext(SizeContext).currentSize;
		const navigationContentbox = React.useContext(NavigationContentboxContext);

		const [currentViewIndex, setCurrentViewIndex] = React.useState<number>(0);

		const views = props.views;
		const mobileSize = ["sm", "xs"].includes(currentSize ?? "");

		React.useEffect(() => {
			if (mobileSize) {
				setCurrentViewIndex(Math.max(views.length - 1, 0));
			}
		}, [views.length, mobileSize]);

		const createVisibleView = (view: View, index: number, width: number): VisibleView => {
			const { name, activityId, ...rest } = view;

			const ViewComponent = props.viewProvider(name);
			const ProgressComponent = props.progressComponentProvider(props.regionReference, name);

			const Content = (
				<MobileNavigationContext.Provider
					value={{
						onBackButtonClicked:
							mobileSize && currentViewIndex !== 0
								? () => setCurrentViewIndex(currentViewIndex - 1)
								: navigationContentbox.onBackButtonClicked,
						onForwardButtonClicked:
							mobileSize && currentViewIndex < views.length - 1
								? () => setCurrentViewIndex(currentViewIndex + 1)
								: undefined
					}}
				>
					<ProgressComponent activityId={activityId}>
						<ViewComponent name={name} activityId={activityId} ariaLevel={index + 1} {...rest} />
					</ProgressComponent>
				</MobileNavigationContext.Provider>
			);

			return {
				element: Content,
				key: `${index}-${name}`,
				width: 6
			};
		};

		const visibleViews: VisibleView[] = [];

		if (!mobileSize) {
			const mainViews = views.filter(v => !EXTRA_VIEW_ACTIVITY_IDS.includes(v.activityId));
			const extraViews = views.filter(v => EXTRA_VIEW_ACTIVITY_IDS.includes(v.activityId));

			const visibleCount = Math.min(mainViews.length, 2) + extraViews.length;
			const width = visibleCount >= 3 ? 4 : 6;

			// Overview always on the left
			if (mainViews.length > 0) {
				const overview = mainViews[0];
				visibleViews.push(createVisibleView(overview, views.indexOf(overview), width));
			}

			// Latest form/detail view on the right
			if (mainViews.length > 1) {
				const latestMain = mainViews[mainViews.length - 1];
				visibleViews.push(createVisibleView(latestMain, views.indexOf(latestMain), width));
			}

			// Optional extra views (e.g. changelog sidebar)
			extraViews.forEach(v => {
				visibleViews.push(createVisibleView(v, views.indexOf(v), width));
			});
		} else if (views.length > currentViewIndex) {
			visibleViews.push(createVisibleView(views[currentViewIndex], currentViewIndex, 6));
		}

		return <MasterDetail visibleViews={visibleViews} animation={{ enabled: false }} />;
	}
};
